import React from 'react';
import { onError } from '@apollo/client/link/error';
import { Observable } from '@apollo/client';
import { message, Modal } from 'antd';
import { getRefreshToken, getUserInfo, saveUserInfo, removeUserInfo } from 'utils/auth';
import { removeUserResourceCodes } from 'utils/resourceAuth';
import { recordListingTooBroad } from 'utils/orderListingHintTracker';
import { t } from '../i18n';

// 定义全局类型
declare global {
  interface Window {
    __fieldErrors?: Record<string, string>;
  }
}

// Token 刷新状态
let isRefreshing = false;
let pendingRequests: Array<{
  resolve: (value?: unknown) => void;
  reject: (reason?: unknown) => void;
}> = [];

const resolvePendingRequests = () => {
  pendingRequests.forEach(({ resolve }) => resolve());
  pendingRequests = [];
};

const rejectPendingRequests = (reason?: unknown) => {
  pendingRequests.forEach(({ reject }) => reject(reason));
  pendingRequests = [];
};

/**
 * 通过 fetch 直接调用 refreshToken mutation，避免循环依赖（errorLink ↔ client）
 */
const fetchRefreshToken = async (refreshToken: string): Promise<{
  success: boolean;
  token?: string;
  refreshToken?: string;
}> => {
  const response = await fetch('/api/graphql', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      query: `mutation RefreshToken($refreshToken: String!) {
        refreshToken(refreshToken: $refreshToken) {
          success
          message
          token
          refreshToken
        }
      }`,
      variables: { refreshToken },
    }),
  });
  const json = await response.json();
  return json.data?.refreshToken ?? { success: false };
};

// F3：Web 单会话被踢下线弹窗（去重，避免并发请求重复弹）
let webSessionKickedDialogShown = false;
// 一旦本会话被判定为「被踢」，抑制后续/并发请求触发的通用未授权提示（如"请登录后继续"）
let webSessionKicked = false;
const showWebSessionKickedOutDialog = (currentPath: string) => {
  if (webSessionKickedDialogShown) return;
  webSessionKickedDialogShown = true;
  Modal.warning({
    title: t('accountSecurity.session.kickedTitle') as string,
    content: React.createElement(
      'div',
      { style: { whiteSpace: 'pre-line' } },
      t('accountSecurity.session.kickedContent') as string,
    ),
    okText: t('accountSecurity.session.kickedOk') as string,
    onOk: () => {
      removeUserResourceCodes();
      removeUserInfo();
      webSessionKickedDialogShown = false;
      webSessionKicked = false;
      if (currentPath !== '/login') {
        window.location.href = '/login';
      }
    },
  });
};

const handleLogout = (currentPath: string, translatedMessage?: string) => {
  if (translatedMessage) {
    message.error(translatedMessage);
  }
  const timeout = setTimeout(() => {
    removeUserResourceCodes();
    removeUserInfo();
    if (currentPath !== '/login') {
      window.location.href = '/login';
    }
    clearTimeout(timeout);
  }, 3000);
};

// 错误处理链接
export const errorLink = onError(({graphQLErrors, networkError, operation, forward}) => {
    if (graphQLErrors) {
        for (const err of graphQLErrors) {
            const code = err.extensions?.code;
            const messageKey = err.extensions?.messageKey;

            // 默认使用错误消息
            let translatedMessage: string = t(messageKey as string) as string;

            const currentPath = window.location.pathname;

            // F3：Web 单会话被踢出 —— 任意错误码下优先弹专用提示并终止本次响应的全部错误处理，
            // 避免与通用未授权提示（"请登录后继续"）叠加；刷新 token 也仍会被踢，故不走刷新。
            if (messageKey === 'errors.auth.webSessionKickedOut') {
                webSessionKicked = true;
                showWebSessionKickedOutDialog(currentPath);
                return;
            }
            // 已被踢后，抑制并发请求触发的通用未授权提示/登出，统一由被踢弹窗收口
            if (webSessionKicked) {
                break;
            }

            // 根据错误码分类处理
            switch(code) {
                case 'UNAUTHORIZED': {
                    // 如果已在登录页，不处理
                    if (currentPath === '/login') break;

                    const refreshToken = getRefreshToken();

                    // 无 refreshToken，直接登出
                    if (!refreshToken) {
                        console.error('UNAUTHORIZED, no refreshToken, path:', currentPath);
                        handleLogout(currentPath, translatedMessage);
                        break;
                    }

                    // 如果正在刷新，排队等待
                    if (isRefreshing) {
                        return new Observable(observer => {
                            pendingRequests.push({
                                resolve: () => {
                                    const userInfo = getUserInfo();
                                    const newToken = userInfo?.token;
                                    if (newToken) {
                                        const oldHeaders = operation.getContext().headers;
                                        operation.setContext({
                                            headers: {
                                                ...oldHeaders,
                                                authorization: `Bearer ${newToken}`,
                                            },
                                        });
                                    }
                                    forward(operation).subscribe(observer);
                                },
                                reject: (error) => {
                                    observer.error(error);
                                },
                            });
                        });
                    }

                    // 开始刷新
                    isRefreshing = true;

                    return new Observable(observer => {
                        fetchRefreshToken(refreshToken).then((result) => {
                            if (result.success && result.token && result.refreshToken) {
                                // 刷新成功，保存新 Token
                                const userInfo = getUserInfo();
                                if (userInfo) {
                                    saveUserInfo({
                                        ...userInfo,
                                        token: result.token,
                                        refreshToken: result.refreshToken,
                                    });
                                }

                                // 更新当前请求的 header 并重试
                                const oldHeaders = operation.getContext().headers;
                                operation.setContext({
                                    headers: {
                                        ...oldHeaders,
                                        authorization: `Bearer ${result.token}`,
                                    },
                                });

                                // 处理排队的请求
                                resolvePendingRequests();

                                // 重试当前请求
                                forward(operation).subscribe(observer);
                            } else {
                                // 刷新失败
                                rejectPendingRequests();
                                handleLogout(currentPath, translatedMessage);
                                observer.error(err);
                            }
                        }).catch(() => {
                            rejectPendingRequests();
                            handleLogout(currentPath, translatedMessage);
                            observer.error(err);
                        }).finally(() => {
                            isRefreshing = false;
                        });
                    });
                }

                case 'PERMISSION':
                    console.error('PERMISSION, path:', currentPath, 'messageKey:', messageKey);
                    // 灰度权限互斥：在新页面收到该错误则跳旧页面，在旧页面则跳新页面
                    if (messageKey === 'errors.permission.routePlanningGrayAccessDenied') {
                        if (currentPath.includes('/split-order')) {
                            // 新线路页面 → 旧线路页面
                            window.location.href = currentPath.replace('/split-order', '/routes');
                        } else if (currentPath.includes('/routes') && !currentPath.includes('/routes-')) {
                            // 旧线路页面 → 新线路页面（排除 routes-history、routes-new 等）
                            window.location.href = currentPath.replace('/routes', '/split-order');
                        } else {
                            message.error(translatedMessage);
                        }
                    } else {
                        message.error(translatedMessage);
                    }
                    break;

                case 'VALIDATION_ERROR': {
                    // 不在此处弹 toast，交由页面 catch 统一处理，避免重复且防止展示 key
                    console.error('VALIDATION_ERROR, path:', currentPath, 'messageKey:', messageKey, 'message:', err.message);
                    break;
                }

                case 'BUSINESS_ERROR':
                    console.error('BUSINESS_ERROR, path:', currentPath, 'messageKey:', messageKey, 'message:', err.extensions?.message || err.message);
                    // 降级熔断：全局提示服务不可用
                    if (messageKey === 'errors.system.serviceUnavailable') {
                        const details = err.extensions?.details as Record<string, string> | undefined;
                        message.error(t(messageKey, { service: details?.service ?? '' }) as string);
                    } else if (messageKey === 'errors.business.order.listingQueryTooBroad') {
                        // 后端 guard 拦截：订单列表查询条件过宽
                        const details = err.extensions?.details as Record<string, string> | undefined;
                        const reason = details?.reason;
                        const fallback = t(messageKey) as string;
                        const text = reason
                            ? (t(`errors.business.order.listingQueryTooBroadByReason.${reason}`, { defaultValue: fallback }) as string)
                            : fallback;
                        message.error(text);
                        recordListingTooBroad();
                    }
                    break;
                case 'INTERNAL_ERROR':
                default:
                    // 系统错误使用error
                    console.error('SYSTEM_ERROR, path:', currentPath, 'messageKey:', messageKey);
                    // 输出完整的错误信息以便调试
                    console.error('Full GraphQL error:', {
                        message: err.message,
                        extensions: err.extensions,
                        path: err.path,
                        locations: err.locations
                    });
                    // 优先显示原始错误消息，如果没有则显示翻译后的消息
                    const displayMessage = err.message || translatedMessage || 'System error occurred';
                    message.error(displayMessage);
                    break;
            }
        }
    }

    if (networkError) {
        message.error(t('errors.system.networkError'));
    }

});
